<template>
  <div>
    <v-skeleton-loader v-if="loading" transition="fade-transition" type="image" height="136" />

    <!-- empty state -->
    <v-row v-else-if="!accounts.length" class="mx-0" align="center" justify="center">
      <v-col class="py-2 text-center" cols="7">
        <v-icon class="my-1 d-block" color="secondary" size="40">mdi-bank</v-icon>
        <span class="my-1 d-block subtitle-1 font-weight-medium">Concilia transferencias con el pago de facturas (sin esfuerzo)</span>
        <span class="my-1 px-8 d-block body-2">Conecta tu cuenta de banco y haz match entre cada transferencia recibida y los pagos de tus clientes.</span>
        <v-btn class="mt-3" @click="initFintocWidget" color="primary">Conectar banco</v-btn>
        <span class="mt-2 d-block caption text--secondary">
          <template v-if="account.country.id === 'CL'">1 UF + IVA</template>
          <template v-else>18.00 USD</template> / mensual por cuenta activa
        </span>
      </v-col>
    </v-row>
    <!-- end empty state -->

    <v-slide-group v-else show-arrows prev-icon="mdi-chevron-left-box" next-icon="mdi-chevron-right-box">
      <!-- bank account card -->
      <v-slide-item v-for="d in accounts" :key="d.id">
        <v-hover v-slot:default="{ hover }" open-delay="200">
          <v-card class="mr-3 " :class="{'background': hover}" @click="openDetail(d)" outlined width="260" height="155" :ripple="false">
            <v-card-text class="pb-0">
              <v-row align="start" no-gutters>
                <img v-if="d.logo" :src="d.logo" :alt="`logo ${d.bankName}`" width="32">
                <v-icon v-else color="secondary" size="32">mdi-bank</v-icon>
              </v-row>
              <span class="mt-2 d-block body-2 text--secondary font-weight-medium text-truncate">{{d.bankName}}</span>
              <span class="d-block body-2 text--primary text-truncate">{{d.label}}</span>
            </v-card-text>
            <v-card-actions class="py-4 px-4">
              <v-chip :color="d.status.color" :text-color="d.status.textColor" small>{{d.status.label}}<v-icon class="ml-1" x-small>{{d.status.icon}}</v-icon></v-chip>
              <v-spacer />
              <span class="caption text--secondary" v-if="d.lastSync">{{d.lastSync | dateFromNow}}</span>
              <span class="caption font-italic text--disabled" v-else>Sin sincronizar</span>
            </v-card-actions>
          </v-card>
        </v-hover>
      </v-slide-item>
      <!-- end bank account card -->

      <!-- connect bank card -->
      <v-slide-item key="connect">
        <v-card class="mr-3" @click="initFintocWidget" outlined width="260" height="155" :ripple="false">
          <v-card-text class="text-center">
            <v-icon class="mt-2 d-block" color="primary" size="32">mdi-plus-circle</v-icon>
            <span class="mt-1 d-block body-2 text--secondary font-weight-medium">Conectar banco</span>
            <span class="mt-1 d-block caption text--secondary">
              <template v-if="account.country.id === 'CL'">1 UF + IVA</template>
              <template v-else>USD 40</template> / mensual por cuenta activa
            </span>
          </v-card-text>
        </v-card>
      </v-slide-item>
      <!-- end connect bank card -->
    </v-slide-group>

    <!-- dialog detail -->
    <v-dialog v-model="dialogDetail" width="600" scrollable>
      <v-card v-if="selected">
        <v-card-title class="pa-0">
          <v-toolbar flat height="55">
            <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Detalles de la cuenta</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-row align="center" justify="center" class="mr-1">
                <v-btn class="ml-3" @click="dialogDetail=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
              </v-row>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>

        <v-divider />

        <v-card-text class="py-4">
          <v-row class="px-3" justify="space-between" align="center">
            <div>
              <img v-if="selected.logo" :src="selected.logo" :alt="`logo ${selected.bankName}`" width="40">
              <v-icon v-else color="secondary" size="40">mdi-bank</v-icon>
              <span class="d-block subtitle-1 text--secondary font-weight-medium">{{selected.bankName}}</span>
              <span class="d-block body-2">
                {{selected.accountType}}
                <template v-if="selected.number"> · Nº {{selected.number}}</template>
              </span>
            </div>
            <div>
              <v-chip class="my-2" :color="selected.status.color" :text-color="selected.status.textColor" small>{{selected.status.label}}<v-icon class="ml-1" x-small>{{selected.status.icon}}</v-icon></v-chip>
            </div>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-text class="py-4">
          <v-list class="transparent">
            <v-list-item class="pa-0">
              <v-list-item-title>
                Sincronizado por última vez el
                <template v-if="selected.lastSync">{{selected.lastSync | dateTime}}</template>
                <template v-else><span class="font-italic text--disabled">Aún no se sincroniza</span></template>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider />

        <v-card-text class="py-4 background">
          <v-list class="transparent">
            <v-list-item class="pa-0">
              <v-list-item-title>Nombre del titular</v-list-item-title>
              <v-list-item-subtitle v-if="selected.raw.holder_name">{{selected.raw.holder_name}}</v-list-item-subtitle>
              <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="pa-0">
              <v-list-item-title>Identificación del titular</v-list-item-title>
              <v-list-item-subtitle v-if="selected.raw.holder_id">{{selected.raw.holder_id}}</v-list-item-subtitle>
              <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="pa-0">
              <v-list-item-title>ID</v-list-item-title>
              <v-list-item-subtitle>
                <v-tooltip right transition="scale-transition">
                  <template v-slot:activator="{on}">
                    <v-btn class="font-weight-regular" @click="toClipboard(selected.id)" text color="secondary" v-on="on" :ripple="false">{{selected.id}}</v-btn>
                  </template>
                  <span>Copiar</span>
                </v-tooltip>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="pa-0">
              <v-list-item-title>Conectado desde</v-list-item-title>
              <v-list-item-subtitle>{{selected.raw.created | dateTime}}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="pa-0">
              <v-list-item-title>Última sincronización</v-list-item-title>
              <v-list-item-subtitle v-if="selected.lastSync">{{selected.lastSync | dateTime}}</v-list-item-subtitle>
              <v-list-item-subtitle class="font-italic text--disabled" v-else>Aún no se sincroniza</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="disconnect(selected)" color="red" dark>Desconectar cuenta</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog detail -->

  </div>
</template>
<script>
import { mapState } from 'vuex'
import GenericViewMixin from '@/mixins/GenericViewMixin'

const FINTOC_SDK = 'https://js.fintoc.com/v1/'

const STATUS = {
  active: { label: 'Conectado', color: 'lightBlue', textColor: 'primary', icon: 'mdi-check' },
  authentication_failed: { label: 'Contraseña incorrecta', color: 'red lighten-5', textColor: 'red', icon: 'mdi-alert-circle' },
  unknown_error: { label: 'Última conexión fallida', color: 'amber lighten-5', textColor: 'amber accent-4', icon: 'mdi-alert-circle' }
}

const STATUS_UNKNOWN = { label: 'Estado desconocido', color: 'defaultGrey', textColor: 'secondary', icon: 'mdi-help-circle' }

// la API entrega account_type en crudo, sin etiqueta traducida
const ACCOUNT_TYPES = {
  checking_account: 'Cuenta corriente',
  savings_account: 'Cuenta de ahorro',
  sight_account: 'Cuenta vista',
  rut_account: 'Cuenta RUT',
  line_of_credit: 'Línea de crédito'
}

export default {
  props: {
    title: {
      type: String,
      default: 'Cuentas bancarias'
    }
  },
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    dialogDetail: false,
    loading: false,
    selected: null,
    widgetFintoc: null
  }),
  computed: {
    ...mapState({
      banksList: state => state.accounts.banksList
    }),
    accounts () {
      return this.banksList.map(d => ({
        raw: d,
        id: d.id,
        logo: this.bankLogo(d),
        bankName: (d.bank || {}).name,
        label: [d.name, d.number].filter(Boolean).join(' '),
        number: d.number,
        accountType: ACCOUNT_TYPES[d.account_type] || 'Cuenta bancaria',
        status: STATUS[d.status] || STATUS_UNKNOWN,
        lastSync: d.origin_refreshed_at
      }))
    }
  },
  created () {
    // la vista se remonta en cada click de paginación o de tab (router-view va
    // keyado en fullPath), así que solo pedimos las cuentas la primera vez de
    // esta organización. El refresco explícito pasa por getList desde @change
    if (this.$store.state.accounts.banksAccount === this.account.id) {
      return false
    }

    this.getList()
  },
  mounted () {
    // warm-up so the first click opens the widget instantly. $loadScript is
    // idempotent and a failure here is not fatal: initFintocWidget retries.
    this.$loadScript(FINTOC_SDK).catch(() => {})
  },
  beforeDestroy () {
    // the view remounts on every status tab click (router-view is keyed on
    // fullPath), so an undestroyed widget leaks an iframe per remount
    if (this.widgetFintoc && typeof this.widgetFintoc.destroy === 'function') {
      this.widgetFintoc.destroy()
    }

    this.widgetFintoc = null
  },
  methods: {
    bankLogo (d) {
      try {
        return require(`@/assets/appAsset-bankingLogo__${(d.bank || {}).api_name}_Min.svg`)
      } catch (error) {
        return null
      }
    },
    getList () {
      // loader: false is load-bearing. VQueryBuilder dispatches accounts/LIST on
      // this same page and TOGGLE_RETRIEVE_LOADER is a toggle, not a set.
      this.loading = !this.banksList.length

      this.$store.dispatch('accounts/LIST', {
        resource: 'banks',
        query: {
          status__in: 'active,authentication_failed,unknown_error'
        },
        loader: false
      })
      .then(() => {
        this.$store.commit('accounts/SET_BANKS_ACCOUNT', this.account.id)
      })
      .catch(() => {})
      .finally(() => {
        this.loading = false
      })
    },
    openDetail (d) {
      this.selected = d
      this.dialogDetail = true
    },
    disconnect (d) {
      this.$dialog.warning({
        title: 'Desconectar cuenta',
        text: `
          <span class="d-block mb-4">Al desconectar la cuenta:</span>
          <span class="d-block pl-6">1. No se seguirán importando los movimientos de esta</span>
          <span class="d-block pl-6">2. Los movimientos ya importados seguirán disponibles</span>
          <span class="d-block my-4">¿Estás seguro de continuar?</span>
          <span class="d-block font-weight-medium">Esta acción no se puede deshacer</span>
        `,
        actions: [
          {
            color: 'red',
            text: 'Continuar',
            handle: () => {
              this.$store.dispatch('accounts/DESTROY', {
                resource: 'banks',
                id: d.id
              })
              .then(() => {
                this.dialogDetail = false
                this.selected = null
                this.$dialog.message.info('La cuenta de banco ha sido desconectada')
                this.$emit('change')
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    },
    async initFintocWidget () {
      try {
        await this.$loadScript(FINTOC_SDK)
      } catch (error) {
        this.$dialog.message.error('No pudimos cargar el conector bancario. Intenta nuevamente.')
        return
      }

      if (!window.Fintoc) {
        this.$dialog.message.error('No pudimos cargar el conector bancario. Intenta nuevamente.')
        return
      }

      if (!this.widgetFintoc) {
        this.widgetFintoc = window.Fintoc.create({
          holderType: 'business',
          product: 'movements',
          publicKey: this.$FINTOC_API_KEY,
          // webhookUrl: `${this.$API_BASE}/banks/?provider=fintoc&external=True&account=${this.account.id}`
          // ^ hardcoded on purpose: $API_BASE is localhost in development and Fintoc's
          //   servers must be able to reach the webhook. Do not "fix" this.
          webhookUrl: `https://api.piriod.com/banks/?provider=fintoc&external=True&account=${this.account.id}`,
          onSuccess: this.onFintocSuccess
        })
      }

      this.widgetFintoc.open()
    },
    onFintocSuccess () {
      this.getList()
      this.$emit('change')
    }
  }
}
</script>
